import { rem } from "@certn/cascadia";
import {
  BasicButton,
  GridItem,
  InputWrapper,
  SearchableSelect,
} from "@certn/cascadia-react";
import { S3FileInfo } from "@certn/cascadia-react/deprecated/upload/types";
import { useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Item } from "react-stately";

import { ApplicantDocumentUpload } from "../../../../components/ApplicantDocumentUpload";

export type CategoryType = "primary" | "secondary";

type DocumentFieldProps = {
  type: CategoryType;
  label: string;
  info: React.ReactNode;
  files: components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"]["value"];
  onChange: (
    files: components["schemas"]["AustralianNpcIdentificationDocumentsCheckField"]["value"]
  ) => void;
  isRequired?: boolean;
  errorMessage?: string;
};

export function DocumentField({
  type,
  label,
  info,
  onChange,
  isRequired,
  ...props
}: DocumentFieldProps) {
  const { t } = useTranslation();
  const { applicantId } = useParams();
  const request = useAPIRequest();
  const { data: categoryTypes } = usePeopleAPI<
    "/internal/people/document-types/",
    Record<CategoryType, { key: string; value: string; points: number }[]>
  >("/internal/people/document-types/", {
    check: "npc",
  });
  const getSubtitle = (documentType: string) =>
    categoryTypes?.[type]?.find(({ key }) => key === documentType)?.value ?? "";
  const getPoints = (documentType: string) =>
    categoryTypes?.[type]?.find(({ key }) => key === documentType)?.points ?? 0;
  const [files, setFiles] = useState<S3FileInfo[]>(
    props.files.map((file) => ({
      document: {
        document_id: file.id,
      } as S3FileInfo["document"],
      type: file.file_type,
      name: file.file_name,
      url: file.get_url,
      subtitle: getSubtitle(file.document_type),
      size: 0,
    }))
  );
  const [documentType, setDocumentType] = useState<string>(
    props.files[0]?.document_type ?? ""
  );

  async function deleteDocument(documentId: string) {
    try {
      await request(
        `/internal/people/applicant/${applicantId}/document/${documentId}/delete`,
        {
          method: "POST",
        }
      );
    } catch (err) {
      console.error("Unable to delete the document:", err);
    }
  }

  return (
    <GridItem className={css({ gap: rem("space-4") })}>
      {files.length === 0 && (
        <InputWrapper span="auto">
          <SearchableSelect
            label={label}
            info={info}
            selectedKey={documentType}
            placeholder={t("Please select")}
            onSelectionChange={(value) => setDocumentType(value as string)}
            isRequired={isRequired}
            testID={`npc-identification-documents-${type}-document-select`}
          >
            {!categoryTypes?.[type] ? (
              <Item key="Loading">{t("Loading...")}</Item>
            ) : (
              categoryTypes[type].map(({ key, value, points }) => (
                <Item
                  key={key}
                  textValue={`${value} (${points} ${t("points")})`}
                >
                  {value} ({points} <Trans>points</Trans>)
                </Item>
              ))
            )}
          </SearchableSelect>
        </InputWrapper>
      )}

      <ApplicantDocumentUpload
        isRequired={isRequired}
        label={files.length > 0 ? label : undefined}
        info={files.length > 0 ? info : undefined}
        documentType={documentType}
        documentSubtype={type}
        applicantId={applicantId ?? ""}
        error={props.errorMessage}
        renderTrigger={(openFileBrowser: () => void) => (
          <BasicButton
            leftIcon="add"
            size="medium"
            isDisabled={!documentType}
            testID={`npc-identification-documents-${type}-add-document-button`}
            onPress={openFileBrowser}
          >
            <Trans>Add document</Trans>
          </BasicButton>
        )}
        files={files}
        setFiles={(files) => {
          setFiles((prevFiles) => {
            if (files.length === 0 && prevFiles.length > 0) {
              deleteDocument(prevFiles[0].document.document_id);
            }

            return files.map((file) => ({
              ...file,
              subtitle: getSubtitle(documentType),
            }));
          });
          onChange(
            files.map(({ document, name, type: file_type }) => ({
              id: document.document_id,
              document_type: documentType,
              document_subtype: type,
              file_name: name,
              file_type,
              get_object_key: "",
              get_url: "",
              points: getPoints(documentType),
            }))
          );
        }}
      />
    </GridItem>
  );
}
